/**
 * QuestionFlowControl
 *
 * P_BRANCHING_TREE_ENGINE_1.2 — per-question "After this question"
 * selector. Applies to EVERY question type EXCEPT branching (branching
 * uses per-choice next_step on each ChoiceOption, configured inside
 * BranchingChoicesEditor).
 *
 * Without this control, the public runtime always linear-advanced
 * after a non-branching answer, which caused branch follow-ups to
 * leak into the next sibling branch. With it, each branch follow-up
 * can terminate (`end`) or rejoin the main survey (`jump`).
 */
import { useTranslation } from 'react-i18next';
import { Label } from '../../../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

const QuestionFlowControl = ({ question, questionIdx, allQuestions, onChange }) => {
    const { t } = useTranslation();
    const ns = question.next_step || { type: 'next' };

    const jumpTargets = (allQuestions || [])
        .filter((q) => q.id !== question.id)
        .map((q) => ({
            id: q.id,
            label: q.text?.en || q.text?.fr || q.text?.es || q.id,
        }));

    const update = (patch) => {
        const merged = { ...ns, ...patch };
        if (merged.type !== 'jump') delete merged.target_question_id;
        onChange(merged.type === 'next' && !merged.target_question_id ? null : merged);
    };

    return (
        <div
            className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900/30 p-3 space-y-2"
            data-testid={`q-flow-${questionIdx}`}
            data-insight-id={questionIdx === 0 ? "surveys.workflow.after-question" : undefined}
            data-insight-label={questionIdx === 0 ? "After this question" : undefined}
        >
            <Label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                {t('surveys.flow.afterLabel', 'After this question')}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 items-center">
                <Select
                    value={ns.type || 'next'}
                    onValueChange={(v) => update({ type: v })}
                >
                    <SelectTrigger data-testid={`q-flow-type-${questionIdx}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="next">{t('surveys.flow.continue', 'Continue to next question')}</SelectItem>
                        <SelectItem value="jump">{t('surveys.flow.jump', 'Jump to another question')}</SelectItem>
                        <SelectItem value="end">{t('surveys.flow.end', 'End survey')}</SelectItem>
                    </SelectContent>
                </Select>
                {ns.type === 'jump' && (
                    <Select
                        value={ns.target_question_id || ''}
                        onValueChange={(v) => update({ target_question_id: v })}
                    >
                        <SelectTrigger data-testid={`q-flow-target-${questionIdx}`}>
                            <SelectValue placeholder={t('surveys.branching.selectTarget', 'Select a question…')} />
                        </SelectTrigger>
                        <SelectContent>
                            {jumpTargets.length === 0 && (
                                <SelectItem value="__none__" disabled>
                                    {t('surveys.branching.noTargets', 'No other questions yet')}
                                </SelectItem>
                            )}
                            {jumpTargets.map((q, qi) => (
                                <SelectItem key={q.id} value={q.id}>
                                    Q{qi + 1} — {q.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
        </div>
    );
};

export default QuestionFlowControl;
